import { DetailedInsight, ExploreSingleInsight } from '@toggle/toggle';
import { isSameDay } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatUtcDate } from '~/services/insights/utils/insight-utils';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';

import { InsightDetails } from '../insight-card/insights-details/InsightDetails';
import * as S from './ExploreInsightCard.styles';
import { IconConfig } from '~/views/insights/components/insights-full-drawer/InsightsFullDrawer';

const SINGLE_ICONS_SIZE = { width: '34px', height: '40px' };
const COMBINE_ICONS_SIZE = { width: '34px', height: '72px' };

export interface ExploreInsightCardProps {
  insight: DetailedInsight;
  exploreInsight: ExploreSingleInsight;
  activeInsightId: string;
  dataInsightId: string;
  ref: (el: HTMLDivElement | null) => void;
  handleSetActiveInsightId: (insightId: string) => void;
  handleSetIconConfig?: (config: IconConfig) => void;
}

export const ExploreInsightCard = ({
  insight,
  exploreInsight,
  activeInsightId,
  handleSetActiveInsightId,
  handleSetIconConfig,
  dataInsightId,
  ref,
}: ExploreInsightCardProps) => {
  const { t } = useTranslation(['insights']);
  const {
    metadata,
    created_at: createAt,
    card,
    episodes,
    simple_title,
  } = insight;

  const isoDate = formatUtcDate(createAt, 'd MMM, yyyy');
  const isNewInsight = isSameDay(new Date(createAt), new Date());
  const episodeLabel = t('insights:insightCard.episode', {
    count: episodes?.length ?? 0,
  });

  const conditions = getConditionIcons(exploreInsight);
  const isCombined = conditions.length > 1;
  const entityTag = insight.metadata.entity;
  const formattedDirection = getExploreDirection(insight.metadata.direction);

  const handleClick = () => {
    handleSetIconConfig?.({
      conditions,
      direction: exploreInsight.direction,
      isCombined,
      layout: 'horizontal',
    });

    handleSetActiveInsightId(insight.id);
  };

  return (
    <S.InsightCardRoot
      data-insight-id={dataInsightId}
      ref={ref}
      $isActive={insight.id === activeInsightId}
      onClick={handleClick}
      data-testid="explore-insight-card"
    >
      <S.IconWrapper>
        <InsightIconsRoot
          conditions={conditions}
          direction={exploreInsight.direction}
          customSize={isCombined ? COMBINE_ICONS_SIZE : SINGLE_ICONS_SIZE}
          isStarred={isNewInsight}
          isCombined={isCombined}
          layout="vertical"
        />
      </S.IconWrapper>
      <S.InfoWrapper>
        <S.InsightDetailsWrapper>
          <InsightDetails
            name={simple_title || card.name}
            horizon={metadata.horizon}
            stars={metadata.stars}
            median={metadata.median_return}
            direction={formattedDirection}
            episodeLabel={episodeLabel}
            entityTag={entityTag}
          />
        </S.InsightDetailsWrapper>
        <S.AnalysisInfo>
          <span>{isoDate}</span>
          {isNewInsight && (
            <S.StyledBadge
              label={t('insights:insightCard.new')}
              data-testid="is-new-badge"
            />
          )}
        </S.AnalysisInfo>
      </S.InfoWrapper>
    </S.InsightCardRoot>
  );
};
